<!-- Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved. -->
<div class="container" appLifecycleTracker trackerName="manage-users">

    <div *ngIf="!(loadingUsers$ | async)">
        <span class="users-data-loaded"></span>
    </div>

    <ng-container *ngIf="shouldShowUsers(); else NotAdminUser">
        <div class="top-container">
            <mat-form-field appearance="outline">
                <input matInput [(ngModel)]="searchTerm" appDebounceInput debounceTime="500"
                       (debounceInput)="getUsers(true)"
                       type="search" placeholder="{{fSearch}}" maxlength="255">
            </mat-form-field>
            @if (user$ | async; as user) {
                <div *ngIf="isEffectiveUserAdmin" class="button-container">
                    @if (user.role == UserRole.ADMIN) {
                        <button id="add-user" mat-flat-button color="primary" (click)="addUser()">{{fAdd}}</button>
                    }
                    <button mat-raised-button color="primary" (click)="exportCSV()">{{'EVI_EXPORT_CSV' | translate}}</button>
                    <button mat-raised-button color="primary" (click)="exportJSON()">{{'EVI_EXPORT_JSON' | translate}}</button>
                        @if (user.role == UserRole.ADMIN) {
                            <button type="button" id="more-actions" mat-raised-button [matMenuTriggerFor]="actionMenu" color="primary"><i class="mdi mdi-dots-horizontal"></i></button>
                            <mat-menu #actionMenu="matMenu">
                                <button id="reactivate-selected-users" [disabled]="!canActivate()" mat-menu-item (click)="reactivateSelectedUsers()">{{fReactivate}}</button>
                                <button id="deactivate-selected-users" [disabled]="!canDeactivate()" mat-menu-item (click)="deactivateSelectedUsers()">{{fDeactivate}}</button>
                                <button id="delete-selected-users" [disabled]="!(canDelete() | async)" mat-menu-item (click)="deleteSelectedUsers()">{{'U_DELETE_USER' | translate}}</button>
                                <button id="force-expire-selected-users" [disabled]="!expirable" mat-menu-item   (click)="expireUsersPasswords()">{{'U_FORCE_EXPIRE_PASSWORD' | translate}}</button>
                            </mat-menu>
                        } @else {
                            <button id="force-expire-selected-users" [disabled]="!expirable" mat-flat-button color="primary" (click)="expireUsersPasswords()">{{'U_FORCE_EXPIRE_PASSWORD' | translate}}</button>
                        }
                </div>
            }
        </div>

        <div class="cigent-box-container">
            <div *ngIf="!(loadingUsers$ | async); else loadingUserCount">
                <strong>{{totalResults}} {{fUsers}}</strong>
            </div>
            <ng-template #loadingUserCount>
                <ngx-skeleton-loader
                  count="1"
                  appearance="line"
                  [theme]="valueStyle">
                </ngx-skeleton-loader>
            </ng-template>
            <app-column-chooser saveId="users" [elements]="columns" (change)="updateDisplayedColumns($event)">
            </app-column-chooser>
        </div>

        <div class="table-container"
             infiniteScroll
             [infiniteScrollDistance]="2"
             [infiniteScrollThrottle]="50"
             (scrolled)="onScrollHandler()"
             [scrollWindow]="false">
            <table mat-table
               [dataSource]="users" matSortActive="username" matSortDirection="asc"
               class="mat-elevation-z0" matSort aria-describedby="List of users"
               (matSortChange)="sortColumn($event)">

                <!-- Checkbox Column -->
                <ng-container matColumnDef="select">
                    <th scope="col" mat-header-cell *matHeaderCellDef>
                        <mat-checkbox (change)="$event ? masterToggle() : null"
                                      [checked]="selection.hasValue() && isAllSelected()"
                                      [indeterminate]="selection.hasValue() && !isAllSelected()"
                                      aria-label="User Selection Checkbox"
                                      color='primary'>
                        </mat-checkbox>
                    </th>
                    <td mat-cell *matCellDef="let row let user">
                        <mat-checkbox (click)="$event.stopPropagation()"
                                      (change)="checkboxToggle($event, row.userData)"
                                      [checked]="selection.isSelected(row.userData)"
                                      [aria-label]="checkboxLabel(row.userData)"
                                      id="userCheckbox_{{user.userData.username}}">
                        </mat-checkbox>
                    </td>
                </ng-container>

                <ng-container matColumnDef="fullname">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fFullName}}</th>
                    <td mat-cell *matCellDef="let user" (keyup.enter)="editUser(user.userData.id)"
                        role="link" tabindex="0" id="fullName_{{user.userData.username}}"
                        attr.aria-label="Press enter to edit user {{user.userData.fname}} {{user.userData.lname}}">
                        {{user.userData.fname}} {{user.userData.lname}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="username">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fUser}}</th>
                    <td mat-cell *matCellDef="let user" id="user_{{user.userData.username}}">
                        {{user.userData.username}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="location">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fLocation}}</th>
                    <td mat-cell *matCellDef="let user" id="location_{{user.userData.username}}">
                        {{user.userData.location}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="jobTitle">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fJob}}</th>
                    <td mat-cell *matCellDef="let user" id="jobTitle_{{user.userData.username}}">
                        {{user.userData.title}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="department">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fDepartment}}</th>
                    <td mat-cell *matCellDef="let user" id="department_{{user.userData.username}}">
                        {{user.userData.department}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="keyStatus">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fKey}}</th>
                    <td mat-cell *matCellDef="let user" id="keyStatus_{{user.userData.username}}">
                        {{getKeyStatusText(user.userData)}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="keySource">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fSource}}</th>
                    <td mat-cell *matCellDef="let user" id="keySource_{{user.userData.username}}">
                        {{user.userData.key_source || fN_A}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="lastLogin">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fLogin}}</th>
                    <td mat-cell *matCellDef="let user" id="lastLogin_{{user.userData.username}}">
                        {{(user.userData.last_login | date: 'medium') || fNever}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="identitySource">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{'IDENTITY_SOURCE' | translate}}</th>
                    <td mat-cell *matCellDef="let user" id="identitySource_{{user.userData.username}}">
                        {{getIdentitySource(user.userData)}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="role">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fRole}}</th>
                    <td mat-cell id="role_{{user.userData.username}}" *matCellDef="let user">
                        @switch (user.userData.role) {
                            @case (UserRole.USER) {
                                {{'APU_USER' | translate}}
                            }
                            @case (UserRole.ADMIN) {
                                {{'APU_ADMIN' | translate}}
                            }
                            @case (UserRole.HELP_DESK) {
                                {{'APU_HELPDESK' | translate}}
                            }
                        }
                    </td>
                </ng-container>

                <ng-container matColumnDef="passwordExp">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{'U_PASSWORD_EXPIRED' | translate}}</th>
                    <td mat-cell id="expiredStatus_{{user.userData.username}}" *matCellDef="let user" matTooltip="{{user.userData.password_exp}}">
                        {{ user.userData.password_exp | expiresIn }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="active">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{fStatus}}</th>
                    <td mat-cell *matCellDef="let user" id="active_{{user.userData.username}}">
                        {{user.userData.active ? fActive : fInactive}}
                    </td>
                </ng-container>

                <ng-container matColumnDef="securityApprover">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{'security-approval' | translate}}</th>
                    <td mat-cell *matCellDef="let user" id="securityApprover_{{user.userData.username}}">
                        @switch (user.userData.security_approver) {
                            @case (SecurityApproverState.APPROVER) {
                                {{ 'AIV_APPROVER' | translate }}
                            }
                            @case (SecurityApproverState.APPROVER_PENDING) {
                                <a (click)="showSecurityApproval(user.userData, $event)">
                                    {{ 'security-approver.pending-removal' | translate }}
                                </a>
                            }
                            @case (SecurityApproverState.NOT_APPROVER_PENDING) {
                                <a (click)="showSecurityApproval(user.userData, $event)">
                                    {{ 'security-approver.pending-approver' | translate }}
                                </a>
                            }
                            @default {
                                {{ 'NONE' | translate }}
                            }
                        }
                    </td>
                </ng-container>
                <ng-container matColumnDef="twoFactor">
                    <th scope="col" mat-header-cell *matHeaderCellDef mat-sort-header>{{'2FA' | translate}}</th>
                    <td mat-cell *matCellDef="let user"
                        id="twoFactor_{{user.userData.username}}">
                        {{user.userData.second_factor_enabled ? ('ENABLED' | translate) : ('DISABLED' | translate)}}
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="getDisplayColumns()"></tr>
                @if (user$ | async; as loggedInUser) {
                    @if (loggedInUser.role == UserRole.ADMIN) {
                        <tr mat-row *matRowDef="let user; columns: getDisplayColumns()" (click)="editUser(user.userData.id)"
                            class='clickable'>
                        </tr>
                    } @else {
                        <tr mat-row *matRowDef="let user; columns: getDisplayColumns()"></tr>
                    }
                }
            </table>
        </div>
    </ng-container>

    <ng-template #NotAdminUser>
        <h2 style="text-align: center" *ngIf="loadingUsers$ | async">{{fNoPermission}}</h2>
    </ng-template>
</div>
